
import { createClient } from '@supabase/supabase-js';
import { User, Language } from '../types';
import { t } from '../i18n';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_API_KEY;

export const supabase = createClient(SUPABASE_URL, SUPABASE_KEY);

const processImage = (file: File | Blob, maxWidth: number, maxHeight: number, quality: number = 0.8): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas context not available'));
        
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Image processing failed'));
        }, 'image/jpeg', quality);
      };
      img.onerror = (e) => reject(e);
    };
    reader.onerror = (e) => reject(e);
  });
};

export const signInWithGoogle = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });
  if (error) throw error;
  return data;
};

export const signInWithKakao = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'kakao' as any,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });
  if (error) throw error;
  return data;
};

export const signInWithNaver = async () => {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'naver' as any,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });
  if (error) throw error;
  return data;
};

export const signInWithEmail = async (email: string, pass: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: email,
    password: pass,
  });
  if (error) throw error;
  return data.user;
};

export const signUpWithEmail = async (email: string, password: string, name: string) => {
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { full_name: name },
      emailRedirectTo: `${window.location.origin}/auth/callback`
    }
  });

  if (authError) throw authError;
  if (!authData.user) throw new Error("User creation failed");

  return authData.user;
};

export const resendVerificationEmail = async (email: string) => {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email,
    options: {
      emailRedirectTo: `${window.location.origin}/callback`
    }
  });
  if (error) throw error;
  return true;
};

export const resetPasswordForEmail = async (email: string) => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/change-password`,
  });
  if (error) throw error;
  return true;
};

export const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
        password: newPassword
    });
    if (error) throw error;
    return true;
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

export const updateNickname = async (userId: string, newName: string): Promise<void> => {
  const { error } = await supabase
    .from('user')
    .update({ name: newName })
    .eq('id', userId);
  if (error) throw error;
};

export const updateUserLanguage = async (userId: string, lang: Language): Promise<void> => {
  const { error } = await supabase
    .from('user')
    .update({ lang: lang })
    .eq('id', userId);
  if (error) throw error;
};

export const syncUserProfile = async (authId: string, email: string, name: string, avatarUrl: string, currentAppLang: Language): Promise<User> => {
  try {
    const { data: existingUser, error: fetchError } = await supabase
      .from('user')
      .select('*')
      .eq('id', authId)
      .maybeSingle();

    if (existingUser) {
      if (!existingUser.lang) {
          await updateUserLanguage(authId, currentAppLang).catch(() => {});
      }
      return {
        id: existingUser.id,
        name: existingUser.name,
        email: existingUser.email,
        avatar: existingUser.avatar_url,
        backgroundUrl: existingUser.background_url,
        isPro: false,
        joinDate: existingUser.created_at,
        lang: existingUser.lang || currentAppLang,
        role: existingUser.role
      };
    }

    const { data: newUser, error: insertError } = await supabase
      .from('user')
      .insert([
        { id: authId, email, name, avatar_url: avatarUrl, lang: currentAppLang, role: 'user', points: 100 }
      ])
      .select()
      .single();

    if (insertError) throw insertError;

    // 회원가입 포인트 로그 추가
    try {
      await supabase.from('point_logs').insert([
        { user_id: authId, amount: 100, reason: t(currentAppLang, 'reasonWelcomePoints') }
      ]);
    } catch (err) {
      console.error("Failed to log welcome points:", err);
    }

    return {
      id: newUser.id,
      name: newUser.name,
      email: newUser.email,
      avatar: newUser.avatar_url,
      backgroundUrl: newUser.background_url,
      isPro: false,
      joinDate: newUser.created_at,
      lang: newUser.lang,
      role: newUser.role
    };
  } catch (e) {
    console.warn("Custom profile sync failed, using auth metadata instead:", e);
    return {
      id: authId,
      name: name,
      email: email,
      avatar: avatarUrl,
      isPro: false,
      joinDate: new Date().toISOString(),
      lang: currentAppLang,
      role: 'user'
    };
  }
};

export const uploadAvatar = async (userId: string, file: File): Promise<string> => {
  try {
    const compressedBlob = await processImage(file, 512, 512, 0.85);
    const fileName = `avatar_${Date.now()}_${Math.random().toString(36).slice(2, 11)}.jpg`;
    const filePath = `avatars/${userId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('xleven')
      .upload(filePath, compressedBlob, {
        upsert: false,
        contentType: 'image/jpeg',
        cacheControl: '3600',
      });

    if (uploadError) throw uploadError;

    // Create signed URL instead of public URL for private bucket access
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('xleven')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry

    if (signedUrlError) throw signedUrlError;

    const avatarUrl = signedUrlData.signedUrl;

    const { error: updateError } = await supabase
      .from('user')
      .update({ avatar_url: avatarUrl })
      .eq('id', userId);

    if (updateError) throw updateError;

    return avatarUrl;
  } catch (e: any) {
    console.error("Avatar upload process failed:", e);
    throw e;
  }
};

export const uploadBackground = async (userId: string, file: File): Promise<string> => {
  try {
    const compressedBlob = await processImage(file, 1200, 800, 0.8);
    const fileName = `bg_${Date.now()}_${Math.random().toString(36).slice(2, 11)}.jpg`;
    const filePath = `avatars/${userId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('xleven')
      .upload(filePath, compressedBlob, {
        upsert: false,
        contentType: 'image/jpeg',
        cacheControl: '3600',
      });

    if (uploadError) throw uploadError;

    // Create signed URL instead of public URL for private bucket access
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('xleven')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry

    if (signedUrlError) throw signedUrlError;

    const backgroundUrl = signedUrlData.signedUrl;

    const { error: updateError } = await supabase
      .from('user')
      .update({ background_url: backgroundUrl })
      .eq('id', userId);

    if (updateError) throw updateError;

    return backgroundUrl;
  } catch (e: any) {
    console.error("Background upload process failed:", e);
    throw e;
  }
};

export const uploadSharedImage = async (userId: string, imageBlob: Blob): Promise<string> => {
  try {
    const compressedBlob = await processImage(imageBlob, 800, 800, 0.6);
    const fileName = `feed_${Date.now()}_${Math.random().toString(36).slice(2, 11)}.jpg`;
    const filePath = `upload/${userId}/feed/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('xleven')
      .upload(filePath, compressedBlob, {
        upsert: false,
        contentType: 'image/jpeg',
        cacheControl: '31536000',
      });

    if (uploadError) {
      console.error("Shared image upload error details:", uploadError);
      throw uploadError;
    }

    // Use 'shared' bucket for signed URL
    const { data: signedUrlData, error: signedUrlError } = await supabase.storage
      .from('xleven')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365); // 1 year expiry

    if (signedUrlError) throw signedUrlError;

    return signedUrlData.signedUrl;
  } catch (e: any) {
    console.error("Shared image upload failed:", e);
    throw e;
  }
};

/**
 * Storage URL에서 파일 경로를 추출합니다.
 */
const getPathFromUrl = (url: string): { bucket: string; path: string } | null => {
  // Signed URL (shared/xleven) check first
  if (url.includes('/storage/v1/object/sign/')) {
    const match = url.match(/\/object\/sign\/([^/]+)\/(.+?)\?/);
    if (match) {
      return { bucket: match[1], path: match[2] };
    }
  }

  // Public URL check
  if (url.includes('/storage/v1/object/public/')) {
    const match = url.match(/\/object\/public\/([^/]+)\/(.+)/);
    if (match) {
      return { bucket: match[1], path: match[2] };
    }
  }

  return null;
};

/**
 * 특정 URL의 파일을 Storage에서 삭제합니다.
 */
export const deleteStorageFileByUrl = async (url: string): Promise<void> => {
  const fileInfo = getPathFromUrl(url);
  if (!fileInfo) return;
  await supabase.storage.from(fileInfo.bucket).remove([fileInfo.path]);
};

/**
 * 특정 폴더 내부의 모든 내용을 재귀적으로 삭제합니다.
 */
export const deleteFolderContents = async (folderPath: string): Promise<void> => {
  try {
    // 1. 해당 경로의 항목들을 나열 (페이지네이션 없이 최대 1000개까지 조회)
    const { data: items, error: listError } = await supabase.storage
        .from('xleven')
        .list(folderPath, { limit: 1000 });

    if (listError || !items || items.length === 0) return;
    
    const filesToRemove: string[] = [];
    
    for (const item of items) {
        // item.name이 '.emptyFolderPlaceholder'인 경우도 포함하여 경로 생성
        const itemPath = folderPath ? `${folderPath}/${item.name}` : item.name;
        
        // Supabase list() 결과에서 id가 없으면 '폴더'로 간주됩니다.
        if (!item.id) {
            await deleteFolderContents(itemPath);
        } else {
            filesToRemove.push(itemPath);
        }
    }

    // 2. 수집된 파일들을 한 번에 삭제
    if (filesToRemove.length > 0) {
        const { error: removeError } = await supabase.storage.from('xleven').remove(filesToRemove);
        if (removeError) {
            console.error("Some files could not be removed in:", folderPath, removeError);
            throw removeError;
        }
    }
  } catch (e) {
    console.warn("Folder cleanup process failed for:", folderPath, e);
    throw e;
  }
};

/**
 * 사용자와 관련된 모든 Storage 데이터를 삭제합니다.
 */
export const deleteUserStorageFolder = async (userId: string): Promise<void> => {
  try {
    await deleteFolderContents(`avatars/${userId}`);
    await deleteFolderContents(`upload/${userId}/feed`);
  } catch (e) {
    console.warn("Storage cleanup failed for user:", userId, e);
  }
};

export const deleteUserAccount = async (userId: string): Promise<void> => {
    const { error: authError } = await supabase.auth.signOut();
    if (authError) throw authError;
};

export const getOracleConfig = () => {
  return {
    host: import.meta.env.VITE_ORACLE_HOST,
    adminKey: import.meta.env.VITE_ORACLE_ADMIN_KEY
  };
};
